import type React from 'react';

import { AdornedInput, Adornment, FormField, FormHelp, Paragraph, Spacer } from '@zillow/constellation';
import { Col, CurrencyInput, Row } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { PETS } from '../../../constants';
import { MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import { ValidationType } from '../../../utils/validators';
import PetsYesAndNoExpansion from './Pets/PetsYesAndNoExpansion';
import PetsInsuranceYesAndNo from './Pets/PetsInsuranceYesAndNo';

const BOUNDS = PETS.BOUNDS.V1;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  pets_isEnabled: false,
  pets_requiresInsuranceCoverage: null,
  pets_rentAmountPerMonth: '',
  pets_nonRefundableFee: '',
  pets_refundableDeposit: '',
};

function Pets_v1({ section, setSectionValues }: Props): JSX.Element {
  function isDepositAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > BOUNDS.MAX_DEPOSIT_AMOUNT) {
      section
        .getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(BOUNDS.MAX_DEPOSIT_AMOUNT));
      return false;
    }
    return true;
  }
  section.setCustomFieldValidator(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, isDepositAmountValid);

  section.setValidation(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH, ValidationType.ZeroCurrencyValidator);
  section.setValidation(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, ValidationType.ZeroCurrencyValidator);
  section.setValidation(PETS.FIELD_NAME.NON_REFUNDABLE_FEE, ValidationType.ZeroCurrencyValidator);
  section.setFieldValue(
    PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH,
    section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH),
  );
  section.setFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT));
  section.setFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE, section.getFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE));

  function onChargesChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;
    const { fieldName } = event.currentTarget.dataset;

    setSectionValues({ [fieldName]: value });
  }

  function onNoPetsClick(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesPetsClick(): void {
    setSectionValues({ pets_isEnabled: true });
  }

  function onNoInsuranceClick(): void {
    setSectionValues({ pets_requiresInsuranceCoverage: false });
  }

  function onYesInsuranceClick(): void {
    setSectionValues({ pets_requiresInsuranceCoverage: true });
  }

  return (
    <PetsYesAndNoExpansion
      yesHandler={onYesPetsClick}
      noHandler={onNoPetsClick}
      yesActive={section.getFieldValue(PETS.FIELD_NAME.IS_ENABLED)}
    >
      <Spacer marginY="sm">
        <PetsInsuranceYesAndNo
          noHandler={onNoInsuranceClick}
          yesActive={section.getFieldValue(PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE)}
          yesHandler={onYesInsuranceClick}
        />
      </Spacer>
      <Spacer marginY="sm">
        <Paragraph fontType="bodySmallHeading">Are there pet charges?</Paragraph>
        <Paragraph>Fill in $0 if none. Typically you should only select one option.</Paragraph>
        <Row paddingY="xs">
          <Col xs={12} sm={6}>
            <Paragraph>Pet rent</Paragraph>
          </Col>
          <Col xs={12} sm={6}>
            <FormField>
              <AdornedInput
                inputId={`AdornedInput-${PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}`}
                error={section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH).hasError}
                input={
                  <CurrencyInput
                    aria-label="Pet rent"
                    data-field-name={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
                    name={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
                    value={section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
                    onChange={onChargesChange}
                    onBlur={onChargesChange}
                  />
                }
                leftAdornment={<Adornment>$</Adornment>}
                rightAdornment={<Adornment>per month</Adornment>}
              />
              <FormHelp>Amount generally should not exceed $75 per pet</FormHelp>
            </FormField>
          </Col>
        </Row>
        <Row paddingY="xs">
          <Col xs={12} sm={6}>
            <Paragraph>Non-refundable fee</Paragraph>
          </Col>
          <Col xs={12} sm={6}>
            <FormField>
              <AdornedInput
                inputId={`AdornedInput-${PETS.FIELD_NAME.NON_REFUNDABLE_FEE}`}
                error={section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).hasError}
                input={
                  <CurrencyInput
                    aria-label="Non-refundable fee"
                    data-field-name={PETS.FIELD_NAME.NON_REFUNDABLE_FEE}
                    name={PETS.FIELD_NAME.NON_REFUNDABLE_FEE}
                    value={section.getFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE)}
                    onChange={onChargesChange}
                    onBlur={onChargesChange}
                  />
                }
                leftAdornment={<Adornment>$</Adornment>}
              />
              {section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).hasError && (
                <FormHelp error>{section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).getErrorMessage()}</FormHelp>
              )}
            </FormField>
          </Col>
        </Row>
        <Row paddingY="xs">
          <Col xs={12} sm={6}>
            <Paragraph>Refundable deposit</Paragraph>
          </Col>
          <Col xs={12} sm={6}>
            <FormField>
              <AdornedInput
                inputId={`AdornedInput-${PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}`}
                error={section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).hasError}
                input={
                  <CurrencyInput
                    aria-label="Refundable deposit"
                    data-field-name={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
                    name={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
                    value={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
                    onChange={onChargesChange}
                    onBlur={onChargesChange}
                  />
                }
                leftAdornment={<Adornment>$</Adornment>}
              />
              {section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).hasError && (
                <FormHelp error>{section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).getErrorMessage()}</FormHelp>
              )}
              <FormHelp>A refundable pet deposit is treated as a security deposit</FormHelp>
            </FormField>
          </Col>
        </Row>
      </Spacer>
    </PetsYesAndNoExpansion>
  );
}

Pets_v1.description = `Pets screen with bounds of ${JSON.stringify(BOUNDS)}`;

export default Pets_v1;
